/**
 * Copyright (c) 2010-2020 California CAD Solutions, Inc. Licensed subject
 * to an End-User License Agreement included in a separate file named EULA.txt
 * that is distributed with this file.
 */
import {
    AfterViewChecked,
    ChangeDetectionStrategy,
    Component, ElementRef,
    EventEmitter,
    inject,
    Input,
    OnDestroy,
    OnInit, output,
    Output, viewChild,
    ViewEncapsulation
} from '@angular/core';
import { FeatureReportCollection, ReportSubtypeValue } from '../../state/report.model';
import { LoggerService } from '../../../shared/services/logger.service';
import { Select, Store } from '@ngxs/store';
import { Observable, Subject } from 'rxjs';
import { map, takeUntil } from 'rxjs/operators';
import { MapConfigService } from '../../../map/services/map-config.service';
import { ReportState } from '../../state/report.state';
import { MatTabChangeEvent, MatTabGroup, MatTab, MatTabContent } from '@angular/material/tabs';
import { RedlineState } from '../../../../components/redline/state/redline.state';
import { MapConfigState } from '../../../map/state/map-config.state';
import { LayoutDirective, FlexDirective } from '@ngbracket/ngx-layout/flex';
import { ReportSelectorComponent } from '../reportSelector/report-selector.component';
import { FeatureReportComponent } from '../../reportTypes/featureReport/feature-report.component';

@Component({
    selector: 'dash-gis-report-side-tabs',
    templateUrl: './report-side-tabs.component.html',
    styleUrls: [ './report-side-tabs.component.scss' ],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    imports: [
        MatTabGroup,
        MatTab,
        LayoutDirective,
        FlexDirective,
        MatTabContent,
        ReportSelectorComponent,
        FeatureReportComponent
    ]
})
export class ReportSideTabsComponent implements OnInit, OnDestroy, AfterViewChecked {
    recordCount = output<number>();
    reportTitle = output<string>();
    activeLayer = output<string>();
    overrideSubtype = output<ReportSubtypeValue>();
    widthUpdate = output<number>();

    @Input() featureReports: FeatureReportCollection;
    @Input() uid: string;

    sortedLayers$: Observable<string[]> = inject(Store).select(MapConfigState.sortedLayers);
    tabHeader = viewChild<ElementRef>('mat-tab-header');
    tabHeaderWidth: number = 140;

    selectedTabIndex = 0;

    private recordCounts: number[] = [];
    private reportTitles: string[] = [];
    private sortedLayers: string[] = [];

    private resizeObserver = new ResizeObserver(this.onTabResize)

    private dispose$ = new Subject<void>();

    constructor(
        private logger: LoggerService,
        private mapConfigService: MapConfigService,
        private store: Store,
        private el: ElementRef
    ) {}

    ngOnInit() {
        if (!this.featureReports) {
            throw new Error('ReportTabsComponent: directive [featureReports] is required');
        }

        this.sortedLayers$.pipe(takeUntil(this.dispose$)).subscribe((sortedLayers) => {
            this.sortedLayers = sortedLayers;
        });
    }

    ngOnDestroy() {
        this.dispose$.next();
    }

    ngAfterViewChecked() {
        if (this.tabHeader() && this.tabHeader()?.nativeElement) {
            this.resizeObserver.observe(this.tabHeader().nativeElement);
        }
    }

    tabIndexChange(evt: MatTabChangeEvent) {
        this.selectedTabIndex = evt.index;
        this.recordCount.emit(this.recordCounts[evt.index]);
        this.reportTitle.emit(this.reportTitles[evt.index]);
        this.activeLayer.emit(this.layerNames[evt.index]);
    }

    setRecordCount(count: number, tabIndex: number) {
        this.recordCounts[tabIndex] = count;

        if (tabIndex === this.selectedTabIndex) {
            this.recordCount.emit(this.recordCounts[tabIndex]);
        }
    }

    setReportTitle(title: string, tabIndex: number) {
        this.reportTitles[tabIndex] = title;

        if (tabIndex === this.selectedTabIndex) {
            this.reportTitle.emit(title);
        }
    }

    getFeatureCount$(layerName: string): Observable<number> {
        return this.store.select(ReportState.featureReportKeys).pipe(
            map((fn) => fn(layerName)),
            map((keys) => keys.length)
        );
    }

    getLayerDisplayName(layerName) {
        const layer = this.mapConfigService.getLayer(layerName);
        if (layer) {
            return layer.label;
        }

        const redlineLayers = this.store.selectSnapshot(RedlineState.viewable);
        for (let i = 0; i < redlineLayers.length; i++) {
            if (redlineLayers[i].name === layerName) {
                return redlineLayers[i].label;
            }
        }
    }

    createUid(...args: any[]) {
        return args.join('_');
    }

    setSubtype(value: ReportSubtypeValue) {
        this.overrideSubtype.emit(value);
    }

    onWidthUpdate(width: number) {
        this.widthUpdate.emit(width + this.tabHeaderWidth);
    }

    get layerNames() {
        return Object.keys(this.featureReports).sort(
            (a, b) => this.sortedLayers.indexOf(a) - this.sortedLayers.indexOf(b)
        );
    }

    private onTabResize(entries: ResizeObserverEntry[]) {
        if (entries.length > 0) {
            this.tabHeaderWidth = entries[0].contentRect.width;
        }

    }
}
